import type { StyleItemCustomEnum } from '@wix/thunderbolt-becky-root'
import { convertStyleProp } from './utils/convertStyleProps'
import type { BuilderStyle } from '@wix/document-services-types'
import type { PathScopedCustomEnums, Style } from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import { resolveSpxAttribute } from '@wix/thunderbolt-commons'
import { CSS_SHORTHAND_PROPERTIES } from './utils/consts'

export const stylePropsToCssVars = (
	style: BuilderStyle,
	pathScopedCustomEnums?: PathScopedCustomEnums,
	componentPath: string = 'root'
): Record<string, string | number> => {
	const { style: styleProps, spx } = style

	const cssVars = Object.entries(styleProps).reduce((acc: Record<string, string | number>, [propName, styleProp]) => {
		if (styleProp.type === 'CustomEnum') {
			Object.assign(
				acc,
				customEnumsToCssVar(
					(styleProp as StyleItemCustomEnum).value,
					propName,
					pathScopedCustomEnums,
					componentPath
				)
			)
			return acc
		}
		const converterVars = convertStyleProp(styleProp, propName, spx)
		Object.assign(acc, converterVars)
		return acc
	}, {})

	return cssVars
}

export const customEnumsToCssVar = (
	propValue: string,
	propName: string,
	pathScopedCustomEnums?: PathScopedCustomEnums,
	componentPath: string = 'root'
) => {
	return {
		[`--${propName}`]: propValue,
		...getAppliedStyles(pathScopedCustomEnums, propName, propValue, componentPath),
	}
}

const getAppliedStyles = (
	pathScopedCustomEnums: PathScopedCustomEnums | undefined,
	propName: string,
	propValue: string,
	componentPath: string
) => {
	return pathScopedCustomEnums?.[componentPath]?.[propName]?.[propValue] ?? {}
}

export const processBuilderStyle = (
	css: Style,
	pathScopedCustomEnums?: PathScopedCustomEnums,
	componentPath: string = 'root'
): Record<string, string | number> => {
	if (!css) {
		return {}
	}

	const result: Record<string, string | number> = {}
	for (const [key, value] of Object.entries(css)) {
		if (key === 'cssCustomProperties') {
			for (const [propName, propValue] of Object.entries(value || {})) {
				const rawVal = `${propValue}`
				const spx = (css as any).spx
				const fixedVal = rawVal.includes('spx') ? resolveSpxAttribute(rawVal, spx, true) : rawVal
				Object.assign(result, customEnumsToCssVar(fixedVal as string, propName, pathScopedCustomEnums, componentPath))
			}
			continue
		}
		if (key === 'cssProperties') {
			const spx = (css as any).spx
			const orderedProps: Array<[string, string]> = Object.entries(value || {}).reduce(
				(acc: Array<[string, string]>, [prop, val]) => {
					const fixedKey = _.kebabCase(prop)
					const rawVal = `${val}`
					const fixedValue = rawVal.includes('spx') ? resolveSpxAttribute(rawVal, spx, true) : rawVal
					const entry: [string, string] = [fixedKey, fixedValue]
					if (CSS_SHORTHAND_PROPERTIES.has(fixedKey)) {
						acc.unshift(entry)
					} else {
						acc.push(entry)
					}
					return acc
				},
				[]
			)
			Object.assign(result, Object.fromEntries(orderedProps))
			continue
		}
		// ignore other keys like id, type, skin, style
	}
	return result
}
